import { forwardRef, useCallback } from 'react';
import cn from 'classnames';
import { noop } from 'lodash';

import IP from 'constants/form/inputParser.const';
import useInputHelpers from 'hooks/useInputHelpers.hook';
import { useTheme } from 'providers/Theme.provider';
import asInput from 'utils/strings/asInput.util';

import CN from './TextInput.module.scss';

const CTAG = 'TextInput';

const TextInput = forwardRef(
  (
    {
      children, // removed from extra, so it is not passed to the <input>
      data,
      disabled,
      value: valueFromProps,
      name,
      onBlur = noop,
      onChange = noop,
      onClick = noop,
      onFocus = noop,
      readOnly,
      tag: tagFromProps,
      variant,
      ...extra
    },
    ref,
  ) => {
    const { isDarkMode } = useTheme();

    const { attributes, value, focus, handle } = useInputHelpers(IP.text, valueFromProps, name, tagFromProps);
    const { received: receivedFocus, lost: lostFocus } = focus;

    const handleChange = useCallback((ev) => handle(`${CTAG}.onChange`, onChange, ev), [handle, onChange]);
    const handleClick = useCallback((ev) => handle(`${CTAG}.onClick`, onClick, ev), [handle, onClick]);

    const handleFocus = useCallback(
      (ev) => {
        receivedFocus();
        return handle(`${CTAG}.onFocus`, onFocus, ev);
      },
      [receivedFocus, handle, onFocus],
    );

    const handleBlur = useCallback(
      (ev) => {
        lostFocus();
        return handle(`${CTAG}.onBlur`, onBlur, ev);
      },
      [handle, lostFocus, onBlur],
    );

    return (
      <input
        inputMode="text"
        type="text"
        {...extra}
        {...attributes}
        data-bem="TextInput"
        className={cn({
          [CN.component]: true,
          [CN[`${variant}Variant`]]: variant,
          [CN.dark]: isDarkMode,
        })}
        value={asInput(value)}
        readOnly={readOnly}
        disabled={disabled}
        onBlur={handleBlur}
        onChange={handleChange}
        onClick={handleClick}
        onFocus={handleFocus}
        ref={ref}
      />
    );
  },
);

TextInput.displayName = 'TextInput';

// noinspection JSUnusedGlobalSymbols
export default TextInput;
